import { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDocuments } from "../../../hooks/useDocuments";
import * as formActions from "../../../store/actions/forms";
import * as appActions from "../../../store/actions/app";
import Pill from "../../ui/pill/Pill";
import styles from "./PropertyForm.module.css";
import {
  states,
  countries,
  Canada,
  expandedStates,
  Mexico,
  US,
} from "../../../constants/data";
import UploadFile from "../../upload-file/UploadFile";
import FileIcon from "../../upload-file/file-icon/FileIcon";
import {
  validationError,
  baseTitle,
  mandatoryFieldsMessage,
} from "../../../constants/messages";
import { useHistory } from "react-router-dom";
import { documentDelivery, yesNo, yesNoUnsure } from "../../../constants/options";
import { dateInRangeIncludesStart, dateToMoment } from "../../../utilities/dateUtilities";
import { COMMERCIAL, PERSONAL, formTypes } from "../../../constants/keys";
import { countryToCode, textToBoolean } from "../../../utilities/dataUtilities";
import inputUtilities from "../../../utilities/forms/formInputUtilities";
import { createValidator, validateFieldset, validatePersonArray } from "../../../utilities/forms/formValidationUtilities";
import { fieldsToStrings, mapPersonArray } from "../../../utilities/forms/formSubmissionUtilities";
import {
  alphabeticTest,
  dateTimeTest,
  emailLengthProps,
  emailTest,
  freeFormTest,
  length100Props,
  length1333Props,
  length50Props,
  phoneProps,
  phoneTest,
  trueFalseRequired,
  univMinDate,
  zipProps,
  zipTest
} from "../../../utilities/validationUtilities";

function PropertyForm() {
  const history = useHistory();
  const dispatch = useDispatch();
  let policyData = useSelector((state) => state.forms.policyRetrievalData);
  let locations = useSelector(
    (state) => state.forms.policyRetrievalData?.locations
  );
  let homePolicies = useSelector((state) => state.forms.homePolicies);
  let propertyPolicies = useSelector((state) => state.forms.propertyPolicies);
  let liabilityPolicies = useSelector((state) => state.forms.liabilityPolicies);
  let activePolicy = useSelector((state) => state.policies.activePolicy);
  let policyType = useSelector((state) => state.app.policyType);
  let lossCauses = useSelector((state) => state.forms.lossCauses);
  let [lossCauseOptions, setLossCauseOptions] = useState(lossCauses);
  let insuredUser = useSelector((state) => state.user.userInfo);
  let config = useSelector((state) => state.app.config);
  let formType = useSelector((state) => state.forms.formType);
  let [policySelectOptions, setPolicySelectOptions] = useState(null);
  let [policies, setPolicies] = useState(
    policyType.active === COMMERCIAL ? propertyPolicies : homePolicies
  );

  const damageTypeOptions = [
    { text: "The insured", value: "insured" },
    { text: "Another party", value: "another" },
    { text: "The insured and another party", value: "both" }
  ];

  const now = new Date();
  now.setSeconds(59);

  const selectStatePlaceholder = "Select a state";
  const rows3Props = { rows: "3" };
  const upToNowProps = { minDate: dateToMoment(univMinDate), maxDate: dateToMoment(now) };

  const policy = {
    policy: {
    label: "Policy",
    value: "",
    displayValue: "",
    required: true,
    }
  };

  const [policyField, setPolicyField] = useState(policy);

  const policyUtilities = inputUtilities(policyField, setPolicyField, styles);
  const createPolicySelect = policyUtilities.createSearchableSelect;
  const handlePolicyChange = policyUtilities.handleFieldChange;

  const fields = {
    whereDate: {
      label: "Date / Time",
      value: null,
      required: true,
      additionalProps: upToNowProps,
      regex: dateTimeTest,
    },
    contactFirstName: {
      label: "Contact First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    contactLastName: {
      label: "Contact Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    contactEmail: {
      label: "Contact Email",
      value: "",
      regex: emailTest,
      additionalProps: emailLengthProps,
    },
    contactPhone: {
      label: "Contact Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    contactDocumentDelivery: {
      label: "What is the preferred method of delivering claim-related documents to the contact?",
      value: "",
    },
    whereAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whereCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereState: {
      label: "State",
      value: "",
      required: true,
      displayValue: null,
    },
    whereCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    whereCountry: {
      label: "Country",
      value: US.name,
    },
    whereLocation: {
      label: "Location",
      value: "",
      displayValue: "",
      required: true,
    },
    witnessesPresent: {
      label: "Were there any witnesses?",
      value: yesNoUnsure[2].value,
    },
    whatHappen: {
      label: "Tell us what happened?",
      value: "",
      required: true,
      regex: freeFormTest,
      additionalProps: { ...rows3Props, ...length1333Props },
    },
    damageType: {
      label: "Who sustained the damage?",
      value: damageTypeOptions[0].value,
      required: true,
    },
    lossCause: {
      label: "Which of the following best describes the cause of loss?",
      value: "",
      required: true,
      displayValue: null
    },
    wasAnyoneInjured: {
      label: "Was Anyone Injured?",
      value: yesNoUnsure[2].value,
    },
    injuredFirstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredLastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    injuredCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredState: {
      label: "State",
      value: "",
      displayValue: "",
    },
    injuredCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    injuredZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest
    },
    injuryDescription: {
      label: "Description of injuries",
      value: "",
      additionalProps: {...rows3Props, ...length1333Props },
      regex: freeFormTest,
    },
    wasTreatmentProvided: {
      label: "Was medical treatment provided?",
      value: false,
    },
    medicalProviderFirstName: {
      label: "Provider First Name",
      value: "",
      additionalProps: length50Props,
      regex: freeFormTest,
    },
    medicalProviderLastName: {
      label: "Provider Last Name",
      value: "",
      additionalProps: length50Props,
      regex: freeFormTest,
    },
    medicalProviderAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    medicalProviderCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    medicalProviderState: {
      label: "State",
      value: "",
      displayValue: "",
    },
    medicalProviderZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    damageToProperty: {
      label: "Was there any other damage to property other than the insureds?",
      value: yesNoUnsure[2].value,
      required: true,
    },
    whoOwnsProperty: {
      label: "Do you know who owns the property?",
      value: false,
      required: true,
      fieldName: "whoOwnsProperty",
      validation: trueFalseRequired
    },
    propertyOwnerFirstName: {
      label: "Owner First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerLastName: {
      label: "Owner Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
    },
    propertyOwnerAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyOwnerCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    propertyOwnerCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    propertyOwnerCountry: {
      label: "Country",
      value: US.name,
    },
    propertyOwnerPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    propertyOwnerDescribeProperty: {
      label: "Describe the property",
      value: "",
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerDamageProperty: {
      label: "Describe the damage to the property",
      value: "",
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerEstimateOfDamage: {
      label: "Estimate of damage?",
      value: "",
      regex: freeFormTest,
    },
  };

  const witness = {
    firstName: {
      label: "Witness First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Witness Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    witnessPhone: {
      label: "Witness Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    }
  };

  let [formFields, setFormFields] = useState(fields);
  let [witnessInfo, setWitnessInfo] = useState([]);

  const [documents, setDocuments] = useState({ files: [], size: 0 });
  const handleDocuments = useDocuments(documents, setDocuments);

  let [witnessObj, setWitnessObj] = useState(null);

  const [validatorsAdded, setValidatorsAdded] = useState(false);

  const {
    createInput,
    createTextArea,
    createDateInput,
    createToggleInput,
    createSelect,
    createSearchableSelect,
  } = inputUtilities(formFields, setFormFields, styles);

  const [enterLocation, setEnterLocation] = useState(false);

  const validateForm = () => {
    if (!validatePersonArray(witnessInfo, setWitnessInfo, true)) {
      return false;
    }
    let pageFields = Object.values(formFields)
      .map((obj, index) => {
        return { ...obj, index: index };
      });
    if (locations?.length < 1 || enterLocation || formFields.whereState.value) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.whereLocation.fieldName);
    } else if (locations?.length > 0) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.whereState.fieldName);
    }
    if (!textToBoolean(formFields.damageToProperty.value) || !formFields.whoOwnsProperty.value) {
      pageFields = pageFields.filter(
        (field) => 
        field.fieldName !== formFields.propertyOwnerLastName.fieldName &&
        field.fieldName !== formFields.propertyOwnerDescribeProperty.fieldName &&
        field.fieldName !== formFields.propertyOwnerDamageProperty.fieldName
     );
    }
    return validateFieldset(pageFields, formFields, setFormFields, true);
  }; //validates fields on the form

  const addValidatorsNames = (theFields, setFields) => {
    let newFields = { ...theFields };
    for (let key in newFields) { 
      if (
        newFields[key].fieldName !== formFields.whoOwnsProperty.fieldName  
      ) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          fieldName: key,
          validation: createValidator({
            ...theField.additionalProps,
            ...theField.regex,
            label: theField.label,
            required: theField.required,
          }),
        };
      }
    }
    setFields(newFields);
  }; //adds validation functions to fields objects, as well as fieldNames 

  const submitForm = () => {
    if (validateForm()) {
      let formData = { ...formFields };
      formData = fieldsToStrings(formData);
      formData.lossCauseFull = formData.lossCause;
      formData.lossCause = lossCauses.find((item) => item.FullText === formData.lossCause)
          ?.Code;
      const location = locations?.find((location) => location.policySystemId_Ext === formData.whereLocation);
      if (location) {
        formData.whereAddress = location.addressLine1;
        formData.whereCity = location.city;
        formData.whereState = location.state;
        formData.whereCounty = "";
        formData.whereZipcode = location.postalCode;
        formData.whereLocationNum = location.locationNumber;
        formData.whereCountry = US.name;
      }
      formData.whereCountry = countryToCode(formData.whereCountry);
      formData.whereProvenanceCanada = formData.whereState;
      formData.whereStateMexico = formData.whereState;
      formData.whereZipcodeCanada = formData.whereZipcode;
      formData.propertyOwnerProvenanceCanada = formData.propertyOwnerState;
      formData.propertyOwnerStateMexico = formData.propertyOwnerState;
      formData.propertyOwnerZipcodeCanada = formData.propertyOwnerZipcode;
      formData.propertyOwnerCountry = countryToCode(formData.propertyOwnerCountry);
      formData.witnesses = mapPersonArray(witnessInfo);
      formData.wasAnyoneInjured = textToBoolean(formData.wasAnyoneInjured);
      formData.injuredparty = [];
      if (formData.wasAnyoneInjured) {
        const injured = {
          firstName: formData.injuredFirstName,
          lastName: formData.injuredLastName,
          injuredPartyAddress: formData.injuredAddress,
          injuredPartyCity: formData.injuredCity,
          injuredPartyState: formData.injuredState,
          injuredPartyCounty: formData.injuredCounty,
          injuredPartyZipcode: formData.injuredZipcode,
          injuredPartyDescriptionOfInjury: formData.injuryDescription,
          wasMedicalTreatmentProvided: formData.wasTreatmentProvided,
          medicalProviderFirstName: formData.medicalProviderFirstName,
          medicalProviderLastName: formData.medicalProviderLastName,
          wasMedicalTreatmentProvidedAddress: formData.medicalProviderAddress,
          wasMedicalTreatmentProvidedCity: formData.medicalProviderCity,
          wasMedicalTreatmentProvidedState: formData.medicalProviderState,
          wasMedicalTreatmentProvidedZipcode: formData.medicalProviderZipcode
        };
        formData.injuredparty.push(injured);
      }
      delete formData.injuredFirstName;
      delete formData.injuredLastName;
      delete formData.injuredAddress;
      delete formData.injuredCity;
      delete formData.injuredState;
      delete formData.injuredCounty;
      delete formData.injuredZipcode;
      delete formData.injuryDescription;
      delete formData.wasTreatmentProvided;
      delete formData.medicalProviderFirstName;
      delete formData.medicalProviderLastName;
      delete formData.medicalProviderAddress;
      delete formData.medicalProviderCity;
      delete formData.medicalProviderState;
      delete formData.medicalProviderZipcode;
      formData.witnessesPresent = textToBoolean(formData.witnessesPresent);
      formData.damageToProperty = textToBoolean(formData.damageToProperty);
      formData.policySuccess = policyData?.policyNumber !== undefined;
      formData.policyNumber = policyField.policy.value.split("/")[0];
      const policyLob =
        activePolicy?.PolicyType ||
        policies.find((item) => item.policyNumber === policyField.policy.value.split("/")[0])
          ?.policyType;
      
      formData.formType = formType;
      if (policyLob === "HOME") {
        formData.formType = formData.damageType === damageTypeOptions[0].value ? formTypes.Home : formTypes.Liability;
      }
      formData.lobCode = formData.formType === formTypes.Liability
        ?`${policyLob.trim()}-L`
        : policyLob.trim();

      const type =
      policyType.active === COMMERCIAL ? "Property/Liability" : "Property";

      dispatch(
        formActions.sendPropertyForm(
          formData,
          documents.files,
          type,
          policyType.active,
          history
        )
      );
      dispatch(formActions.resetClearForm());
    } else {
      dispatch(appActions.setAlertModal(true, "error", validationError));
      setTimeout(() => {
        dispatch(appActions.setAlertModal(false, ""));
      }, 5000);
    }
  };

  useEffect(() => {
    if (formType === formTypes.Property) {
      setPolicies(propertyPolicies);
    } else if (formType === formTypes.Home) {
      setPolicies(homePolicies);
    } else {
      setPolicies(liabilityPolicies);
    }
  }, [
    propertyPolicies,
    homePolicies,
    liabilityPolicies,
    policyType.active,
    formType,
  ]);

  useEffect(() => {
    if (validatorsAdded) {
      // If agent selected policy display only one policy option, otherwise all the policies for an insured
      if (activePolicy) {
        policies = [
          {
            policyNumber: activePolicy.PolicyNumber,
            policyEffectiveDate: activePolicy.PolicyEffectiveDate,
            policyType: activePolicy.PolicyType,
          },
        ];
        let options = policies.map((policy) => {
          return {
            value: `${policy.policyNumber}/${policy.policyEffectiveDate}`,
            label: `${policy.policyNumber}/${policy.policyType}`
          };
        });
        handlePolicyChange(options[0].value, policyField.policy, true, options[0]);
        setPolicySelectOptions(options);
      } else {
        let options = policies.map((policy) => {
          return {
            value: `${policy.policyNumber}/${policy.policyEffectiveDate}`,
            label: `${policy.policyNumber}/${policy.policyType}`
          };
        });
        setPolicySelectOptions(options);
      }
    }
  }, [activePolicy, policies, validatorsAdded]);

  useEffect(() => {
    if (!activePolicy) {
      dispatch(formActions.getStates());
    }
  }, [dispatch, activePolicy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const type = formType || policyType.active;
    document.title = `${baseTitle} - Report ${type} Claim`;
  }, [policyType.active]);

  useEffect(() => {
    if (policyField.policy.value && formType && lossCauses?.length > 0) {
      let theCauses = [...lossCauses];
      const policyLob =
        activePolicy?.PolicyType ||
        policies.find((item) => item.policyNumber === policyField.policy.value.split("/")[0])
          ?.policyType;
      if (policyType.active === PERSONAL) {
        if (formFields.damageType.value === damageTypeOptions[0].value) {
          theCauses = theCauses.filter((cause) => cause.Property);
        } else {
          theCauses = theCauses.filter((cause) => cause.HomeLiability);
        }
        if (
          formFields.lossCause.value &&
          !theCauses.find((cause) => cause.FullText === formFields.lossCause?.value)
        ) {
          
          const theFields = {...formFields};
          theFields.lossCause.value = null;
          setFormFields(theFields);
        }
      } else if (policyLob?.trim() === "GAC") {
        theCauses = theCauses.filter((cause) => cause.Auto);
      } else if (formType === formTypes.Property) {
        theCauses = theCauses.filter((cause) => cause.Property);
      } else if (formType === formTypes.Liability) {
        theCauses = theCauses.filter((cause) => cause.Liability);
      }
      setLossCauseOptions(theCauses);
    }
  }, [formType, lossCauses, policyField.policy.value, policies, formFields.damageType.value]);

  useEffect(() => {
    dispatch(formActions.clearPolicyAssets());
    setFormFields(fields);
    setWitnessInfo([]);
    setValidatorsAdded(false);
    setDocuments({ files: [], size: 0 });
  }, [policyField.policy.value]);//clears data on change in policy number

  useEffect(() => {
    if (policyField.policy.value && formFields.whereDate.value) {
      const policyNumber = policyField.policy.value.substring(0, 7);
      if (
        policyData?.policyNumber !== policyNumber ||
        !dateInRangeIncludesStart(
          formFields.whereDate.value,
          policyData.effectiveDate,
          policyData.expirationDate
        )
      ) {
        //only retrieve again if actual change in policy or term
        dispatch(
          formActions.policySearchRetrieve(
            formFields.whereDate.value.format("YYYY-MM-DD"),
            policyNumber
          )
        );
        dispatch(appActions.setLoading(true));
      }
    } else {
      dispatch(formActions.clearPolicyAssets());
    } //if policy or datetime set to blank, clear policy
  }, [dispatch, formFields.whereDate.value, policyField.policy.value]);

  useEffect(() => {
    if (formFields && !validatorsAdded) {
      addValidatorsNames(policyField, setPolicyField);
      addValidatorsNames(formFields, setFormFields);
      addValidatorsNames(witness, setWitnessObj);
      setValidatorsAdded(true);
    }
  }, [formFields, validatorsAdded]); //eslint-disable-line react-hooks/exhaustive-deps
  //adds validators to fields

  const populatePersonArray = (personObj, setPersonInfo) => {
    const persons = [];
    for (let i = 0; i < 4; i++) {
      const thePerson = {...personObj};
      for (let fieldName in thePerson) {
        const labelSegments = thePerson[fieldName].label.split(" ");
        thePerson[fieldName] = {
          ...thePerson[fieldName],
          fieldName: `${thePerson[fieldName].fieldName}${i}`,
          label: `${labelSegments[0]} ${i + 1} ${labelSegments.filter((item, index) => index !== 0).join(" ")}`
        }
      }
      persons.push(thePerson);
    }
    setPersonInfo(persons);
  };//helper to populate witnesses array

  useEffect(() => {
    if (witnessObj && validatorsAdded && witnessInfo?.length < 1) {
      populatePersonArray(witnessObj, setWitnessInfo);
    }
  }, [witnessObj, validatorsAdded])//populate witnesses array

  useEffect(() => {
    const theFields = {...formFields};
    theFields.whereAddress.value = "";
    theFields.whereCity.value = "";
    theFields.whereState.value = "";
    theFields.whereCounty.value = "";
    theFields.whereZipcode.value = "";
    theFields.whereLocation.value = "";
    theFields.whereCountry.value = US.name;
    setFormFields(theFields);
  }, [enterLocation]);

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.whereCountry.value === Canada.name) {
      theFields.whereState.label = "Province";
    } else {
      theFields.whereState.label = "State";
    }
    theFields.whereState.value = "";
    theFields.whereState.displayValue = "";
    setFormFields(theFields);
  },[formFields.whereCountry.value])//handles change in province/state label based on location country

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.propertyOwnerCountry.value === Canada.name) {
      theFields.propertyOwnerState.label = "Province";
    } else {
      theFields.propertyOwnerState.label = "State";
    }
    theFields.propertyOwnerState.value = "";
    theFields.propertyOwnerState.displayValue = "";
    setFormFields(theFields);
  },[formFields.propertyOwnerCountry.value])//handles change in province/state label based on property owner country

  const createPersonFactory = (personArray, setFunc, personObj) => {
    return personArray.map((person, index) => {
      const setPerson = (input) => {
        const oldPersonInfo = [...personArray];
        oldPersonInfo[index] = input;
        setFunc(oldPersonInfo);
      };
      const createPersonInput = inputUtilities(
        person,
        setPerson,
        styles
      ).createInput;
      const fieldNames = Object.keys(personObj)
      return (
        <div className={`formRow ${styles.personContainer}`} key={index}>
          {fieldNames.map((fieldName) => 
            createPersonInput(person[fieldName], `${100/fieldNames.length}%`)
          )}
        </div>
      );
    });
  };//display fields for each person in an array of person objects

  return (
    <div className={styles.container}>
      <div className="policyBar">
        {policySelectOptions &&
        createPolicySelect(
          policyField.policy,
          "33%",
          policySelectOptions,
          false,
          policySelectOptions?.length === 0 ? "" : "Select a Policy",
          true
          )}
        <h3 className="titleText">Let's collect some details</h3>
      </div>
      {policyField.policy.value && (
        <>
          <span className="redMessage">{mandatoryFieldsMessage}</span>
          <form className="mainForm">
            <div className="formColumn">
              <Pill text="When" />
              <div className="formRow">
                {createDateInput(formFields.whereDate, "100%")}
              </div>

              <>
                <Pill text="Contact" />

                <div className="formRow">
                  {createInput(formFields.contactFirstName, "50%")}
                  {createInput(formFields.contactLastName, "50%")}
                </div>
                <div className="formRow">
                  {createInput(formFields.contactEmail, "50%")}
                  {createInput(formFields.contactPhone, "50%")}
                </div>
                {insuredUser && (
                  <div className="formRow">
                    {createSelect(formFields.contactDocumentDelivery, "100%", documentDelivery)}
                  </div>
                )}
              </>

              <Pill text="Where" />
              {locations?.length > 0 ? (
                <>
                  <button
                    type="button"
                    className={styles.toggleButton}
                    onClick={() => setEnterLocation(!enterLocation)}
                  >
                    {enterLocation
                      ? "Select policy location"
                      : "Enter location details"}
                  </button>
                  <div className="formRow">
                 
                    {locations?.length > 0 && !enterLocation ? (
                      createSearchableSelect(
                        formFields.whereLocation,
                        "70%",
                        locations.map((location) => {
                          return {
                            label: `${location.locationNumber || ""} - ${
                              location.addressLine1 || ""
                            }, ${location.city || ""}, ${
                              location.state || ""
                            }, ${location.postalCode || ""}`,
                            value: location.policySystemId_Ext,
                          };
                        }),
                        false,
                        "Select a location",
                        true
                      )
                    ) : null}
                  </div>
                </>
              ) : null}
              {!(locations?.length > 0) || enterLocation ? (
                <>
                  <div className="formRow">
                    {createInput(formFields.whereAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.whereCity, "33%")}
                    {formFields.whereCountry.value === US.name && (
                      <Fragment>
                        {createSearchableSelect(formFields.whereState, "33%", states, false, selectStatePlaceholder)}
                        {createInput(formFields.whereCounty, "33%")}
                      </Fragment>
                    )}
                    {formFields.whereCountry.value === Mexico.name && (
                      <Fragment>
                        {createInput(formFields.whereState, "67%")}
                      </Fragment>
                    )}
                    {formFields.whereCountry.value === Canada.name && (
                      <Fragment>
                        {createSearchableSelect(
                          formFields.whereState,
                          "67%",
                          expandedStates.filter((state) => state.country.code === Canada.code)
                          .map((province) => {
                            return {
                              label: province.state,
                              value: province.Abbr
                            };
                          }),
                          false,
                          "Select a Province",
                          true
                        )}
                      </Fragment>
                    )}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.whereZipcode, "50%")}
                    {createSelect(
                      formFields.whereCountry,
                      "50%",
                      countries.map((country) => country.name)
                      )}
                  </div>
                </>
              ) : null}

              <Pill text="What" />
              <div className="formRow">
                {createTextArea(formFields.whatHappen, "100%")}
              </div>
              {policyType.active === PERSONAL && (
                <div className="formRow">
                  {createToggleInput(formFields.damageType, "100%", damageTypeOptions)}
                </div>
              )}
              <div className="formRow">
                {createSearchableSelect(formFields.lossCause, "100%", lossCauseOptions.map(cause => cause.FullText), false)}
              </div>

              <div className="formRow">
                {createToggleInput(formFields.wasAnyoneInjured, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.wasAnyoneInjured.value) && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createInput(formFields.injuredFirstName, "50%")}
                    {createInput(formFields.injuredLastName, "50%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.injuredAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.injuredCity, "33%")}
                    {createSearchableSelect(
                      formFields.injuredState,
                      "33%",
                      states,
                      false,
                      selectStatePlaceholder
                      )}
                    {createInput(formFields.injuredCounty, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.injuredZipcode, "33%")}
                  </div>
                  <div className="formRow">
                    {createTextArea(formFields.injuryDescription, "100%")}
                  </div>
                  <div className="formRow">
                    {createToggleInput(formFields.wasTreatmentProvided, "60%", yesNo)}
                  </div>
                  {formFields.wasTreatmentProvided.value && (
                    <div className={styles.hiddenDiv}>
                      <div className="formRow">
                        {createInput(formFields.medicalProviderFirstName, "50%")}
                        {createInput(formFields.medicalProviderLastName, "50%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.medicalProviderAddress, "100%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.medicalProviderCity, "33%")}
                        {createSearchableSelect(
                          formFields.medicalProviderState,
                          "33%",
                          states,
                          false,
                          selectStatePlaceholder
                          )}
                        {createInput(formFields.medicalProviderZipcode, "33%")}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="formRow">
                {createToggleInput(formFields.witnessesPresent, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.witnessesPresent.value) && (
                <div className={styles.hiddenDiv}>
                  {witnessObj && createPersonFactory(witnessInfo, setWitnessInfo, witnessObj)}
                </div>
              )}
            </div>

            <div className="formColumn">
              <Pill text="Property Damage" />
              <div className="formRow">
                {createToggleInput(formFields.damageToProperty, "60%", yesNoUnsure)}
              </div>

              {textToBoolean(formFields.damageToProperty.value) && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createToggleInput(formFields.whoOwnsProperty, "60%", yesNo)}
                  </div>
                  {formFields.whoOwnsProperty.value && (
                    <div className={styles.hiddenDiv}>
                      <div className="formRow">
                        {createInput(formFields.propertyOwnerFirstName, "50%")}
                        {createInput(formFields.propertyOwnerLastName, "50%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.propertyOwnerAddress, "100%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.propertyOwnerCity, "33%")}
                        {createSearchableSelect(formFields.propertyOwnerState, "33%", states, true, selectStatePlaceholder)}
                        {createInput(formFields.propertyOwnerCounty, "33%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.propertyOwnerZipcode, "33%")}
                      </div>
                      <div className="formRow">
                        {createTextArea(formFields.propertyOwnerDescribeProperty, "100%")}
                      </div>
                      <div className="formRow">
                        {createTextArea(formFields.propertyOwnerDamageProperty, "100%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.propertyOwnerEstimateOfDamage, "100%")}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <UploadFile
                getFiles={(files) => handleDocuments(files, "add")}
                size={(
                  documents.size / config.totalFileSizeLimitDivisor
                ).toFixed(2)}
              />

              <div className="documentContainer">
                {documents.files &&
                  documents.files.map((document, index) => {
                    return (
                      <FileIcon
                        key={index}
                        file={document}
                        removeFile={(file) => handleDocuments(file, "remove")}
                      />
                    );
                  })}
              </div>

              <button
                className={styles.reportButton}
                type="button"
                onClick={() => submitForm()}
              >
                Report Your Claim
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default PropertyForm;
